/**
 * Farmacia (clientul) și punctul ei de lucru — perechea din Acțiuni (sampling) și Expuneri.
 *
 * Clientul se alege cu câmpul comun de parteneri (CRM → ExpertAccounts → ANAF, „+ Adaugă partener
 * nou"); punctul, cu căutarea de puncte, restrânsă la clientul ales. „+ Punct nou" adaugă un punct
 * clientului ales și îl trimite în EA (spec parteneri comuni, §5–§7).
 *
 * Sincronizarea merge în ambele sensuri, ca înainte: punctul știe de ce client ține și îl
 * completează, iar schimbarea clientului scoate un punct care nu mai e al lui — perechea
 * „punct Dr.Max + client ALFAELLAS" era posibilă și nu însemna nimic.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { CautaAdaugaPartener } from '../parteneri/CautaAdaugaPartener'
import { AddBranchModal } from '../orders/AddBranchModal'
import { PunctLucruSelect, type PunctLucru } from './PunctLucruSelect'

const BAZA = '/marketing/sampling'

export function FarmaciaSiPunctul({
  clientId,
  punct,
  onClient,
  onPunct,
  etichetaClient,
  ajutorClient,
  ajutorPunct,
}: {
  clientId: number | null
  punct: PunctLucru | null
  onClient: (id: number | null) => void
  onPunct: (p: PunctLucru | null) => void
  etichetaClient: string
  ajutorClient?: string
  ajutorPunct?: string
}) {
  const [punctNou, setPunctNou] = useState(false)
  const farmacii = useQuery({
    queryKey: ['sampling', 'farmacii'],
    queryFn: async () => (await api.get<{ id: number; name: string }[]>(`${BAZA}/farmacii`)).data,
  })
  const toti = (farmacii.data ?? []).map((f) => ({ id: f.id, nume: f.name }))
  const numeClient = toti.find((c) => c.id === clientId)?.nume

  function alegePunct(p: PunctLucru | null) {
    onPunct(p)
    if (p?.clientId && p.clientId !== clientId) onClient(p.clientId)
  }

  return (
    <>
      <div className="block">
        <span className="text-sm font-medium text-b4l-text">{etichetaClient}</span>
        <div className="mt-1">
          <CautaAdaugaPartener
            baza={BAZA}
            toti={toti}
            value={clientId != null ? { id: clientId, nume: numeClient ?? '…' } : null}
            onChange={(p) => {
              // Un client adus din EA sau creat acum nu e încă în lista încărcată.
              if (p && !toti.some((c) => c.id === p.id)) void farmacii.refetch()
              onClient(p?.id ?? null)
              if (punct && p && punct.clientId !== p.id) onPunct(null)
            }}
          />
        </div>
        {ajutorClient && (
          <span className="mt-1 block text-xs text-b4l-text-muted">{ajutorClient}</span>
        )}
      </div>

      <div className="block">
        <span className="flex items-center justify-between text-sm font-medium text-b4l-text">
          <span>Punctul de lucru</span>
          {clientId != null && (
            <button
              type="button"
              className="text-xs font-medium text-b4l-primary hover:underline"
              onClick={() => setPunctNou(true)}
            >
              + Punct nou
            </button>
          )}
        </span>
        <PunctLucruSelect
          value={punct?.id ?? null}
          etichetaCurenta={punct?.eticheta ?? null}
          clientId={clientId}
          onChange={alegePunct}
        />
        {ajutorPunct && (
          <span className="mt-1 block text-xs text-b4l-text-muted">{ajutorPunct}</span>
        )}
      </div>

      <AddBranchModal
        open={punctNou}
        clientId={clientId}
        endpoint={clientId != null ? `${BAZA}/parteneri/${clientId}/puncte` : undefined}
        onClose={() => setPunctNou(false)}
        onCreated={(_id, p) => {
          setPunctNou(false)
          if (p) alegePunct(p)
        }}
      />
    </>
  )
}
